import { FieldPath } from '@form-ts/core';
import { useFormField, useFormWatch } from '@form-ts/react';
import { pipe } from 'fp-ts/function';
import React, { useCallback } from 'react';
import { FormattedMessage } from 'react-intl';
import { Input, InputProps } from 'src/modules/common/components/Input';
import { FormError } from 'src/modules/form/types/FormError';

type Props<TData> = Omit<InputProps,
| 'id'
| 'invalid'
| 'invalidText'
| 'value'
| 'onChange'
> & {
  readonly field: FieldPath<TData, FormError, string>;
  readonly renderError?: (error: FormError) => React.ReactNode;
};

export const TextField: <TData>(props: Props<TData>) => React.ReactElement = (
  {
    field,
    onBlur,
    renderError = renderErrorDefault,
    ...props
  },
) => {
  const { meta, value, error } = useFormField(field);

  const submitted = useFormWatch(field.form, field.form.submitted.get);
  const invalid = error !== undefined && (meta.touched || submitted);

  const handleChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    field.form.change(pipe(
      field.form.currentState,
      field.value.set(event.target.value),
    ));
  }, [field]);

  const handleBlur: React.FocusEventHandler<HTMLInputElement> = useCallback((e) => {
    field.form.change(pipe(
      field.form.currentState,
      field.touched.set(true),
    ));

    onBlur?.(e);
  }, [field, onBlur]);

  return (
    <Input
      {...props}
      id={`${field.form.name}.${field.path}`}
      name={field.path}
      invalid={invalid}
      invalidText={error && invalid ? renderError(error) : null}
      description={props.description === undefined ? (
        <FormattedMessage
          id="form/helper/maxLength"
          values={{ actualLength: value.length, maxLength: props.maxLength }}
        />
      ) : props.description}
      value={value}
      onChange={handleChange}
      onBlur={handleBlur}
    />
  );
};

const renderErrorDefault = (error: FormError): React.ReactNode => (
  <FormattedMessage id={`form/error/${error.code}`} values={error.context}/>
);
